import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LogoutButton } from '@/components/LogoutButton';
import { AuthApiError, getAuthClient } from '@/api/client';
import { errorMessage } from '@/lib/errorMap';
import { applyLoginRedirect } from '@/lib/redirect';
import { useAuthStore } from '@/stores/useAuthStore';

/**
 * Phase 5.5c·5.5d + 5c.C — MFA enrollment 화면 (관리자 강제, 일반 사용자도 enable 가능).
 *
 * 흐름 (Phase 5c.C — multi-channel):
 * 0. 사용자가 method 선택 (TOTP / 이메일 / [SMS·카카오 알림톡: 등록 신청 중])
 * 1. method='totp' → `/v1/auth/mfa/setup` (no body) → secret + otpauth_uri 반환 → QR
 *    method='email' → recipient 입력 → `/v1/auth/mfa/setup` body 전달 → 6자리 OTP 가 메일로
 * 2. 6자리 OTP 입력 → `/v1/auth/mfa/enable` → 활성화 + backup codes 8개
 * 3. 사용자 확인 후 logout → 재로그인 (mfa_pending=false 토큰)
 */
type Method = 'totp' | 'email';
type Phase = 'choose' | 'recipient' | 'verify' | 'backup';

const METHODS: { value: Method; label: string; desc: string; available: boolean }[] = [
  {
    value: 'totp',
    label: 'Authenticator 앱 (TOTP)',
    desc: 'Google · Microsoft · Authy · 1Password 등의 OTP 앱',
    available: true,
  },
  {
    value: 'email',
    label: '이메일',
    desc: '6자리 코드를 등록한 이메일로 받음 (5분 유효)',
    available: true,
  },
];
const FUTURE_METHODS = [
  { label: 'SMS', desc: '카카오 알림톡 등록 후 활성 예정 (2026-05-04 신청)' },
  { label: '카카오 알림톡', desc: '비즈니스 채널 승인 진행 중' },
];

export function MfaSetupPage() {
  const [params] = useSearchParams();
  const redirect = params.get('redirect') ?? '/';
  const [phase, setPhase] = useState<Phase>('choose');
  const [method, setMethod] = useState<Method>('totp');
  const [recipient, setRecipient] = useState('');
  const [recipientHint, setRecipientHint] = useState<string | null>(null);
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [initBusy, setInitBusy] = useState(false);
  const navigate = useNavigate();
  const refreshMe = useAuthStore((s) => s.refreshMe);

  useEffect(() => {
    // 페이지 진입 시 mfa_already_enabled 체크 (sky-check). 단순화 위해 method 선택 후 setup 시 catch.
  }, [navigate, redirect]);

  const submitSetup = async () => {
    setInitBusy(true);
    try {
      const body: { method: Method; recipient?: string } = { method };
      if (method === 'email') body.recipient = recipient.trim();
      const data = await getAuthClient().mfaSetup(body);
      if (method === 'totp') {
        setSecret(data.secret ?? '');
        setOtpauthUri(data.otpauth_uri ?? '');
      } else {
        setRecipientHint(data.recipient_hint);
      }
      setPhase('verify');
    } catch (e) {
      if (e instanceof AuthApiError) {
        if (e.code === 'mfa_already_enabled') {
          toast.info('이미 MFA 가 활성화돼 있습니다.');
          applyLoginRedirect(redirect, navigate);
          return;
        }
        toast.error(errorMessage(e.code));
      } else {
        toast.error('초기화 실패');
      }
    } finally {
      setInitBusy(false);
    }
  };

  const onChooseMethod = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (method === 'totp') {
      void submitSetup();
    } else {
      setPhase('recipient');
    }
  };

  const onRecipientSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (recipient.trim().length === 0) {
      toast.error('수신지를 입력하세요');
      return;
    }
    void submitSetup();
  };

  const onVerify = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await getAuthClient().mfaEnable(code.trim());
      setBackupCodes(data.backup_codes);
      setPhase('backup');
      toast.success('MFA 가 활성화됐습니다');
    } catch (e) {
      if (e instanceof AuthApiError) toast.error(errorMessage(e.code));
      else toast.error('인증 실패');
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirmBackup = async () => {
    toast.info('보안을 위해 다시 로그인해주세요.');
    await getAuthClient().logout();
    await refreshMe();
    navigate('/login', { replace: true });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background p-4">
      <LogoutButton />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>2단계 인증 등록</CardTitle>
          <CardDescription>
            관리자 계정은 등록이 필수, 일반 사용자도 권장됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {phase === 'choose' && (
            <form onSubmit={onChooseMethod} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label>인증 방법 선택</Label>
                {METHODS.map((m) => (
                  <label
                    key={m.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 hover:bg-accent/50 ${
                      method === m.value ? 'border-primary bg-primary/5' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="method"
                      value={m.value}
                      checked={method === m.value}
                      onChange={() => setMethod(m.value)}
                      disabled={!m.available}
                      className="mt-1"
                    />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{m.label}</span>
                      <span className="text-xs text-muted-foreground">{m.desc}</span>
                    </div>
                  </label>
                ))}
                {FUTURE_METHODS.map((m) => (
                  <div
                    key={m.label}
                    className="flex items-start gap-3 rounded-md border bg-muted/30 p-3 opacity-60"
                  >
                    <input type="radio" disabled className="mt-1" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">
                        {m.label}{' '}
                        <span className="text-xs font-normal text-muted-foreground">
                          (준비 중)
                        </span>
                      </span>
                      <span className="text-xs text-muted-foreground">{m.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
              <Button type="submit" disabled={initBusy}>
                {initBusy ? '준비 중...' : '다음'}
              </Button>
            </form>
          )}

          {phase === 'recipient' && (
            <form onSubmit={onRecipientSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="recipient">
                  {method === 'email' ? '인증 이메일' : '수신지'}
                </Label>
                <Input
                  id="recipient"
                  type={method === 'email' ? 'email' : 'text'}
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder={method === 'email' ? 'you@example.com' : ''}
                  autoFocus
                  required
                />
                <p className="text-xs text-muted-foreground">
                  로그인 시 6자리 인증 코드가 이 {method === 'email' ? '이메일' : '수신지'}로 발송됩니다.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPhase('choose')}
                  className="flex-1"
                >
                  뒤로
                </Button>
                <Button type="submit" disabled={initBusy} className="flex-1">
                  {initBusy ? '코드 발송 중...' : '코드 받기'}
                </Button>
              </div>
            </form>
          )}

          {phase === 'verify' && (
            <form onSubmit={onVerify} className="flex flex-col gap-4">
              {method === 'totp' && (
                <div className="flex flex-col items-center gap-3 rounded-md border bg-muted/30 p-4">
                  <div className="rounded bg-white p-3">
                    <QRCodeSVG value={otpauthUri} size={192} level="M" />
                  </div>
                  <p className="text-center text-xs text-muted-foreground">
                    Authenticator 앱에서 위 QR 코드를 스캔하세요.
                  </p>
                  <details className="w-full text-xs">
                    <summary className="cursor-pointer text-muted-foreground">
                      QR 스캔 안 될 때 수동 입력용 secret 보기
                    </summary>
                    <code className="mt-2 block break-all rounded bg-background p-2 font-mono text-[11px]">
                      {secret}
                    </code>
                  </details>
                </div>
              )}
              {method === 'email' && (
                <div className="rounded-md border bg-muted/30 p-4 text-sm">
                  <p>
                    <span className="font-medium">{recipientHint ?? recipient}</span> 으로 6자리
                    인증 코드를 발송했습니다.
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    5분 안에 입력해주세요. 도착하지 않으면 스팸함을 확인하세요.
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-2">
                <Label htmlFor="code">6자리 OTP 코드</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              </div>
              <Button type="submit" disabled={submitting || code.length < 6}>
                {submitting ? '활성화 중...' : '활성화'}
              </Button>
            </form>
          )}

          {phase === 'backup' && (
            <div className="flex flex-col gap-4">
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                <p className="font-medium">백업 코드 8개 — 1회만 표시됩니다.</p>
                <p className="mt-1">
                  각 코드는 1회용. 안전한 곳에 보관하세요. {method === 'totp' ? 'Authenticator 앱' : '이메일'}을
                  잃었을 때 로그인용으로 사용합니다.
                </p>
              </div>
              <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
                {backupCodes.map((c) => (
                  <li key={c} className="rounded border bg-background p-2 text-center">
                    {c}
                  </li>
                ))}
              </ul>
              <Button onClick={() => void onConfirmBackup()} className="mt-2">
                저장 완료 — 다시 로그인
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
